import { useState, useRef, useEffect } from "react"

interface School {
  id: number
  name: string
}

interface SchoolSearchInputProps {
  schools: School[]
  value: string
  onChange: (name: string) => void
  placeholder?: string
  isSelected?: boolean
}

export function SchoolSearchInput({ 
  schools, 
  value, 
  onChange, 
  placeholder = "Type to search...",
  isSelected = false 
}: SchoolSearchInputProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Filter schools based on query
  const filteredSchools = query.trim()
    ? schools.filter(s => s.name.toLowerCase().includes(query.toLowerCase())).slice(0, 10)
    : []

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(0)
  }, [query])

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && isOpen && filteredSchools.length > 0) {
      const highlighted = listRef.current.children[highlightedIndex] as HTMLElement
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex, isOpen, filteredSchools.length])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || filteredSchools.length === 0) {
      // Open dropdown on arrow down if there's a query
      if (e.key === 'ArrowDown' && query.trim()) {
        setIsOpen(true)
        return
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev < filteredSchools.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0)
        break
      case 'Enter':
        e.preventDefault()
        if (filteredSchools[highlightedIndex]) {
          selectSchool(filteredSchools[highlightedIndex].name, true)
        }
        break
      case 'Tab':
        // Select on Tab - let browser handle focus movement
        if (filteredSchools[highlightedIndex]) {
          selectSchool(filteredSchools[highlightedIndex].name, false)
        }
        // Don't prevent default - let Tab naturally move to next input
        break
      case 'Escape':
        setIsOpen(false)
        setQuery(value)
        break
    }
  }

  function selectSchool(name: string, moveFocus: boolean) {
    onChange(name)
    setQuery('')
    setIsOpen(false)
    
    // Only manually move focus for Enter key (Tab will naturally move focus)
    if (moveFocus) {
      const inputs = document.querySelectorAll<HTMLInputElement>('input[type="text"]')
      const currentIndex = Array.from(inputs).indexOf(inputRef.current!)
      if (currentIndex >= 0 && currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus()
      }
    }
  }

  function handleFocus() {
    setQuery(value)
    if (value) {
      setIsOpen(true)
    }
  }

  function handleBlur() {
    // Delay to allow click on dropdown items
    setTimeout(() => {
      setIsOpen(false)
      setQuery('')
    }, 150)
  }

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? query : value}
        onChange={(e) => {
          setQuery(e.target.value)
          setIsOpen(true)
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full px-2 py-1.5 border rounded text-sm ${
          isSelected
            ? 'border-green-300 bg-green-50' 
            : 'border-gray-200 bg-white'
        }`}
      />
      
      {/* Dropdown */}
      {isOpen && filteredSchools.length > 0 && (
        <div 
          ref={listRef}
          className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {filteredSchools.map((school, index) => (
            <button
              key={school.id}
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault()
                selectSchool(school.name, true)
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                index === highlightedIndex
                  ? 'bg-[#1e3a5f] text-white'
                  : 'hover:bg-gray-100'
              }`}
            >
              {school.name}
            </button>
          ))}
        </div>
      )}
      
      {isOpen && query.trim() && filteredSchools.length === 0 && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
        </div>
      )}
    </div>
  )
}
